import { useQueries } from '@tanstack/react-query';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import EntitlementController from '@jwp/ott-common/src/controllers/EntitlementController';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { MediaProduct } from '@jwp/ott-common/types/media';
import { useMemo } from 'react';

const notifyOnChangeProps = ['data' as const, 'isLoading' as const];
const EMPTY_MEDIA_OFFERS: MediaProduct[] = [];

type UseEntitlementResult = {
  mediaid?: string;
  isEntitled: boolean;
  isLoading: boolean;
  mediaOffers: MediaProduct[];
};

function useEntitlement(playlistItems: PlaylistItem | undefined): UseEntitlementResult;
function useEntitlement(playlistItems: PlaylistItem[]): UseEntitlementResult[];
function useEntitlement(playlistItems: PlaylistItem | PlaylistItem[] | undefined): UseEntitlementResult | UseEntitlementResult[] {
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);
  const entitlementController = getModule(EntitlementController, false);

  const playlistItemsArray = useMemo(() => {
    return Array.isArray(playlistItems) ? playlistItems : playlistItems ? [playlistItems] : [];
  }, [playlistItems]);

  const entitlement = useQueries({
    queries: playlistItemsArray.map((playlistItem) => ({
      queryKey: ['entitlements', playlistItem?.mediaid, user?.id, subscription?.subscriptionId],
      queryFn: () => entitlementController?.checkEntitlement(playlistItem),
      enabled: !!playlistItem && !!entitlementController,
      refetchOnMount: 'always' as const,
      notifyOnChangeProps,
    })),
    combine: (results) =>
      playlistItemsArray.map((playlistItem, index) => ({
        mediaid: playlistItem?.mediaid,
        isEntitled: results[index]?.data ?? false,
        isLoading: results[index]?.isLoading ?? false,
        mediaOffers: playlistItem?.mediaOffers || EMPTY_MEDIA_OFFERS,
      })),
  });

  if (!playlistItems) {
    return { isEntitled: false, isLoading: false, mediaOffers: EMPTY_MEDIA_OFFERS, mediaid: undefined };
  }

  if (!Array.isArray(playlistItems)) {
    return entitlement[0];
  }

  return entitlement;
}

export default useEntitlement;
